import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    InputSignal,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ManagementListHeaderComponent } from '@connect-services/client/components/management/management-list-header/management-list-header.component';
import { ButtonComponent, ButtonType } from '@connect-services/client/lib/components/button/button.component';
import { InputLargeTextComponent } from '@connect-services/src/lib/components/input/text/input-large-text/input-large-text.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { NotificationService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormGroupType } from '@shared/client/models/form.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export enum NotificationTypeEnum {
    ALERT = 'alert',
    INFORMATION = 'information',
    UPDATE = 'update',
    WARNING = 'warning',
}

export interface NotificationForm extends FormGroupType {
    body: FormControl<string>;
    title: FormControl<string>;
    type: FormControl<NotificationTypeEnum | undefined>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        InputLargeTextComponent,
        InputTextComponent,
        ManagementListHeaderComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-notification-form',
    templateUrl: './notification-form.component.html',
})
export class NotificationFormComponent implements OnInit {
    public readonly formGroup: WritableSignal<FormGroup<NotificationForm> | null> = signal(null);
    public readonly isAllOperatorSelected: InputSignal<boolean> = input.required<boolean>();
    public readonly selectedFieldInterventionIds: InputSignal<string[] | undefined> = input<string[]>();
    public readonly selectedOperatorIds: InputSignal<string[] | undefined> = input<string[]>();

    protected readonly ButtonType = ButtonType;
    protected notificationTypeOption: SelectOption<NotificationTypeEnum>[] = Object.values(NotificationTypeEnum).map(
        (value: NotificationTypeEnum) => ({
            label: value.toUpperCase(),
            value: value,
        }),
    );

    private readonly notificationService = inject(NotificationService);
    private readonly selectedNotificationType: WritableSignal<NotificationTypeEnum | null> = signal(null);
    private readonly snackBarService = inject(SnackBarService);

    public initForm(): FormGroup<NotificationForm> {
        return new FormGroup<NotificationForm>({
            body: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.required],
            }),
            title: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.maxLength(50), Validators.required],
            }),
            type: new FormControl<NotificationTypeEnum | undefined>(undefined, {
                nonNullable: true,
                validators: [Validators.required],
            }),
        });
    }

    public ngOnInit(): void {
        this.formGroup.set(this.initForm());
    }

    public async sendNotification(): Promise<void> {
        try {
            await firstValueFrom(
                this.notificationService.notificationControllerSendNotification({
                    details: this.formGroup()!.controls.body.value,
                    interventions: this.selectedFieldInterventionIds()?.length
                        ? this.selectedFieldInterventionIds()
                        : undefined,
                    operators:
                        this.selectedFieldInterventionIds()?.length || this.isAllOperatorSelected()
                            ? undefined
                            : this.selectedOperatorIds(),
                    title: this.formGroup()!.controls.title.value,
                    type: this.formGroup()!.controls.type.value!,
                }),
            );

            this.formGroup()?.reset();
            this.formGroup()?.controls.type.setValue(this.selectedNotificationType() ?? undefined);
            this.snackBarService.openSuccessSnackBar('notificationSentSuccessfully');
        } catch {
            this.snackBarService.openErrorSnackBar('notificationSentError');
        }
    }

    protected selectNotificationType(type: NotificationTypeEnum[]): void {
        this.formGroup()?.controls.type.setValue(type[0]);
        this.selectedNotificationType.set(type[0]);
    }
}
